import { useQuery } from '@apollo/client';
import { Timelapse } from '@mui/icons-material';
import { CircularProgress, ListItem } from '@mui/material';
import {
  DateFormatPreset,
  ExtendedListItemText,
  ExtendedListProps,
  formatDate,
  Widget,
  WidgetHeader,
  WidgetIcon,
  ExtendedListItemButton,
} from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';

import {
  RuleEvaluationLogQuery,
  TsrctRuleEvaluationLogOrderField,
  TsrctSortDirection,
} from '@/generated/gql/graphql.ts';
import { I18nNamespace } from '~/common/types';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { VirtualListDynamicHeight } from '~/components/VirtualListDynamicHeight/VirtualListDynamicHeight.tsx';
import { ResponsiveContainerStyled } from '~/modules/RuleEvaluationLogWidget/RuleEvaluationLogWidget.styled.tsx';
import { RouteId } from '~/routes';

import { RULE_EVALUATION_LOG_QUERY } from './RuleEvaluationLogQuery.gql';

type ItemData = RuleEvaluationLogQuery['tesseract']['rule']['evaluationLogs']['nodes'][number];
const EMPTY_ITEMS: ItemData[] = [];

export const RuleEvaluationLogWidget = () => {
  const { entityId } = useModuleContext();
  const { logId } = useParams() as { logId: string };
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'RuleEvaluationLogWidget' });

  const { data, loading } = useQuery(RULE_EVALUATION_LOG_QUERY, {
    variables: {
      ruleId: entityId,
      order: [{ field: TsrctRuleEvaluationLogOrderField.createdAt, sort: TsrctSortDirection.desc }],
      limit: 1000,
    },
  });
  const items = data?.tesseract.rule.evaluationLogs.nodes || EMPTY_ITEMS;

  const renderItem: NonNullable<ExtendedListProps<ItemData>['renderItem']> = useMemo(
    () =>
      ({ item }) => {
        const { id, sequenceNumber, createdAt } = item;
        return (
          <ListItem disablePadding>
            <ExtendedListItemButton
              component={Link}
              to={RouteId.ruleEvaluationLogDetail(entityId, id)}
              selected={id === logId}
            >
              <ExtendedListItemText
                primary={t('itemTitle', { sequenceNumber })}
                caption={formatDate(DateFormatPreset.TWO_DIGIT_TIME, new Date(createdAt))}
              />
            </ExtendedListItemButton>
          </ListItem>
        );
      },
    [t, logId, entityId],
  );

  return (
    <Widget>
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon icon={<Timelapse fontSize="small" />} />}
        actionsLeft={loading ? <CircularProgress size={20} /> : undefined}
      />
      <ResponsiveContainerStyled>
        {({ width, height }) => (
          <VirtualListDynamicHeight width={width} height={height} itemSize={90} items={items} renderItem={renderItem} />
        )}
      </ResponsiveContainerStyled>
    </Widget>
  );
};
